import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { MapPin, Search, Store, Phone, IndianRupee, Layers, ExternalLink, RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/apiErrors';
import api from '../../services/api';

// Map component using Leaflet
const RetailersMap = ({ retailers, center, zoom }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    // Load Leaflet CSS
    if (!document.querySelector('link[href*="leaflet"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    // Load Leaflet JS
    const loadLeaflet = async () => {
      if (!window.L) {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => initMap();
        document.head.appendChild(script);
      } else {
        initMap();
      }
    };

    const initMap = () => {
      if (mapInstanceRef.current || !mapRef.current || !window.L) return;

      mapInstanceRef.current = window.L.map(mapRef.current).setView(
        [center.lat, center.lng],
        zoom
      );

      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors'
      }).addTo(mapInstanceRef.current);

      updateMarkers();
    };

    loadLeaflet();

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  const updateMarkers = useCallback(() => {
    if (!mapInstanceRef.current || !window.L) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Add new markers
    retailers.forEach(retailer => {
      if (retailer.lat && retailer.lng) {
        const color = retailer.outstanding > 0 ? '#ef4444' : '#10b981';
        const icon = window.L.divIcon({
          className: 'custom-marker',
          html: `<div style="
            width: 32px;
            height: 32px;
            background: ${color};
            border-radius: 50%;
            border: 3px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            display: flex;
            align-items: center;
            justify-content: center;
          ">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            </svg>
          </div>`,
          iconSize: [32, 32],
          iconAnchor: [16, 32],
        });

        const marker = window.L.marker([retailer.lat, retailer.lng], { icon })
          .addTo(mapInstanceRef.current)
          .bindPopup(`
            <div style="min-width: 200px; padding: 4px;">
              <h3 style="font-weight: 600; font-size: 14px; margin: 0 0 8px 0;">${retailer.name}</h3>
              ${retailer.phone ? `<p style="font-size: 12px; color: #666; margin: 4px 0;"><strong>Phone:</strong> ${retailer.phone}</p>` : ''}
              ${retailer.area ? `<p style="font-size: 12px; color: #666; margin: 4px 0;"><strong>Area:</strong> ${retailer.area}</p>` : ''}
              <p style="font-size: 12px; color: ${retailer.outstanding > 0 ? '#ef4444' : '#10b981'}; margin: 4px 0; font-weight: 500;">
                ${retailer.outstanding > 0 ? `Outstanding: ₹${retailer.outstanding.toLocaleString('en-IN')}` : 'Clear'}
              </p>
            </div>
          `);

        markersRef.current.push(marker);
      }
    });

    // Fit bounds if there are markers
    if (markersRef.current.length > 0) {
      const group = window.L.featureGroup(markersRef.current);
      mapInstanceRef.current.fitBounds(group.getBounds().pad(0.1));
    }
  }, [retailers]);

  useEffect(() => {
    updateMarkers();
  }, [retailers, updateMarkers]);

  return <div ref={mapRef} className="w-full h-full rounded-lg" />;
};

const MyRetailersMapPage = () => {
  const [loading, setLoading] = useState(true);
  const [retailers, setRetailers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRetailer, setSelectedRetailer] = useState(null);

  const fetchRetailers = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/staff/self/my-retailers-map');
      setRetailers(data.retailers || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load retailers'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRetailers();
  }, [fetchRetailers]);

  const filteredRetailers = useMemo(() => retailers.filter(retailer => {
    if (statusFilter !== 'all') {
      if (statusFilter === 'outstanding' && !(retailer.outstanding > 0)) return false;
      if (statusFilter === 'clear' && retailer.outstanding > 0) return false;
    }
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      return (
        retailer.name?.toLowerCase().includes(search) ||
        retailer.phone?.toLowerCase().includes(search) ||
        retailer.area?.toLowerCase().includes(search)
      );
    }
    return true;
  }), [retailers, statusFilter, searchQuery]);

  const retailersWithLocation = filteredRetailers.filter(r => r.lat && r.lng);

  const stats = {
    total: retailers.length,
    withLocation: retailers.filter(r => r.lat && r.lng).length,
    withOutstanding: retailers.filter(r => r.outstanding > 0).length,
    clear: retailers.filter(r => !r.outstanding || r.outstanding === 0).length,
  };

  const mapCenter = useMemo(() => {
    if (retailersWithLocation.length > 0) {
      const lats = retailersWithLocation.map(r => r.lat);
      const lngs = retailersWithLocation.map(r => r.lng);
      return {
        lat: lats.reduce((a, b) => a + b, 0) / lats.length,
        lng: lngs.reduce((a, b) => a + b, 0) / lngs.length,
      };
    }
    return { lat: 20.5937, lng: 78.9629 }; // Default to India center
  }, [retailersWithLocation]);

  const openInGoogleMaps = (retailer) => {
    if (retailer.lat && retailer.lng) {
      window.open(`https://www.google.com/maps?q=${retailer.lat},${retailer.lng}`, '_blank');
    }
  };

  return (
    <div className="h-full flex flex-col bg-[#F7F8FA]">
      {/* Fixed Page Header */}
      <div className="flex-shrink-0 bg-[#F7F8FA] px-6 pt-5 pb-4">
        <div className="max-w-[1600px] mx-auto">
          {/* Title Row */}
          <div className="flex items-start justify-between mb-5">
            <div>
              <h1 className="text-xl font-semibold text-slate-800 tracking-tight">Retailers Map</h1>
              <p className="text-[13px] text-slate-500 mt-0.5 font-normal">View your assigned retailers on the map</p>
            </div>
            <button
              onClick={fetchRetailers}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white text-slate-700 rounded-lg hover:bg-slate-50 transition-all text-[13px] font-medium shadow-sm border border-slate-200 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {/* Stats Pills */}
          <div className="flex items-center gap-3 mb-5">
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-slate-50 flex items-center justify-center">
                <Store className="w-3.5 h-3.5 text-slate-500" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Total</p>
                <p className="text-[14px] font-semibold text-slate-700">{stats.total}</p>
              </div>
            </div>
            
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-blue-50 flex items-center justify-center">
                <MapPin className="w-3.5 h-3.5 text-blue-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">On Map</p>
                <p className="text-[14px] font-semibold text-blue-600">{stats.withLocation}</p>
              </div>
            </div>
            
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-emerald-50 flex items-center justify-center">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Clear</p>
                <p className="text-[14px] font-semibold text-emerald-600">{stats.clear}</p>
              </div>
            </div>
            
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-red-50 flex items-center justify-center">
                <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Outstanding</p>
                <p className="text-[14px] font-semibold text-red-600">{stats.withOutstanding}</p>
              </div>
            </div>
          </div>

          {/* Search & Filters Bar */}
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search retailers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full max-w-md pl-9 pr-4 py-2 bg-white rounded-lg text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:outline-none transition-all placeholder:text-slate-400 shadow-sm"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-white rounded-lg text-[13px] text-slate-700 border-0 shadow-sm focus:ring-2 focus:ring-teal-500/20 focus:outline-none"
            >
              <option value="all">All Status</option>
              <option value="clear">Clear</option>
              <option value="outstanding">With Outstanding</option>
            </select>
          </div>
        </div>
      </div>

      {/* Map and List Area */}
      <div className="flex-1 overflow-hidden px-6 pb-5">
        <div className="max-w-[1600px] mx-auto h-full">
          <div className="h-full flex gap-4">
            {/* Map Section */}
            <div className="flex-1 bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                    <p className="text-[13px] text-slate-500 font-normal">Loading map...</p>
                  </div>
                </div>
              ) : retailersWithLocation.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                    <MapPin className="w-7 h-7 text-slate-400" />
                  </div>
                  <p className="text-[15px] font-medium text-slate-700">No retailers with location</p>
                  <p className="text-[13px] text-slate-500 mt-1">Add location to your retailers to see them on the map</p>
                </div>
              ) : (
                <RetailersMap 
                  retailers={filteredRetailers} 
                  center={mapCenter} 
                  zoom={12} 
                />
              )}
            </div>

            {/* Retailer List Sidebar */}
            <div className="w-80 flex-shrink-0 bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden flex flex-col">
              <div className="px-4 py-3 border-b border-slate-100">
                <h3 className="text-[14px] font-semibold text-slate-800">Retailers ({filteredRetailers.length})</h3>
              </div>
              <div className="flex-1 overflow-y-auto">
                {filteredRetailers.map(retailer => (
                  <div
                    key={retailer.id}
                    onClick={() => setSelectedRetailer(retailer)}
                    className={`px-4 py-3 border-b border-slate-50 cursor-pointer hover:bg-slate-50 transition-colors ${
                      selectedRetailer?.id === retailer.id ? 'bg-teal-50' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-medium text-slate-800 truncate">{retailer.name}</p>
                        <p className="text-[12px] text-slate-500 truncate mt-0.5">{retailer.area || 'No area'}</p>
                        {retailer.phone && (
                          <p className="text-[11px] text-slate-400 mt-1">{retailer.phone}</p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          retailer.outstanding > 0 
                            ? 'bg-red-50 text-red-700' 
                            : 'bg-emerald-50 text-emerald-700'
                        }`}>
                          {retailer.outstanding > 0 ? `₹${(retailer.outstanding/1000).toFixed(1)}K` : 'Clear'}
                        </span>
                        {retailer.lat && retailer.lng && (
                          <button
                            onClick={(e) => { e.stopPropagation(); openInGoogleMaps(retailer); }}
                            className="p-1 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors"
                            title="Open in Google Maps"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyRetailersMapPage;
